import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import VerifyEmailModal from "@/components/modals/VerifyEmailModal";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { CHANGE_EMAIL_WARNING_MSG, UPDATE_PROFILE_DESCRIPTION } from "@/utils/constants/appConstants/UpdateEmailConstants";
import { DISPOSABLE_EMAIL_ADDRESS, INVALID_EMAIL_ADDRESS } from "@/utils/constants/messages";
import { getFromLocalStorage, isDisposableEmail, isEmail } from "@/utils/functions/helperFunctions";
import AppButton from "@/components/buttons/AppButton";
import { useLocation, useNavigate } from "react-router-dom";
import { getemail_address, getUserId } from "@/utils/functions/TokenFunction";
import { sendCodeToVerifyCurrentEmail, sendUpdateEmailCode, validateUserCurrentEmail } from "@/services/user.service";
import { IS_DISPOSABLE_EMAIL_ALLOWED } from "@/constants/allconstants";

interface UpdateEmailProps {
    show?: boolean;
    onHide?: () => void;
    setShowEditEmailModal: (show: boolean) => void;
    setEmailAddress: (email: string) => void;
}

const UpdateEmail = (props: UpdateEmailProps) => {
    const [newEmail, setNewEmail] = useState("");
    const [code, setCode] = useState("");
    const [codeErrorMsg, setCodeErrorMsg] = useState("");
    const [newEmailErrorMsg, setNewEmailErrorMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showVerifyEmailModal, setShowVerifyEmailModal] = useState(false);
    const [currentEmailToken, setCurrentEmailToken] = useState("");
    const [updateEmailToken, setUpdateEmailToken] = useState("");
    const [screenState, setScreenState] = useState("update-email-instruction");
    const loginWithSSO = getFromLocalStorage("loginWithSSO");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        setCode("");
        if (location?.state?.canEdit === true) {
            props.setShowEditEmailModal(true);
            setScreenState("update-email");
        }
    }, []);

    const handleContinueToEmail = () => {
        if (loginWithSSO) {
            setScreenState("verify-current-email");
            sendCodeToVerifyCurrentEmail(
                { email: getemail_address() },
                (res: any) => setCurrentEmailToken(res.token),
                (err: any) => console.log(err, "err")
            );
        } else {
            navigate(`/validate-user`);
        }
    };

    const handleVerifyCurrentEmailCode = () => {
        if (code === "") {
            setCodeErrorMsg("Please enter the verification code");
            return;
        }
        validateUserCurrentEmail(
            { token: currentEmailToken, code },
            (res: any) => {
                setScreenState("update-email");
                setUpdateEmailToken(res);
            },
            (err: any) => console.log(err, "errverify")
        );
    };

    const handleHideModal = () => {
        setScreenState("update-email-instruction");
        props.onHide?.();
        setNewEmail("");
        navigate(location.pathname, { replace: true, state: {} });
    };

    const handleSendUpdateEmailCode = () => {
        if (!isEmail(newEmail)) {
            setNewEmailErrorMsg(INVALID_EMAIL_ADDRESS);
            return;
        }
        if (isDisposableEmail(newEmail) && !IS_DISPOSABLE_EMAIL_ALLOWED) {
            setNewEmailErrorMsg(DISPOSABLE_EMAIL_ADDRESS);
            return;
        }
        setNewEmailErrorMsg("");
        setIsLoading(true);
        sendUpdateEmailCode(
            updateEmailToken || location?.state?.token,
            { new_email: newEmail, old_email: getemail_address(), user_id: getUserId() },
            (res: any) => {
                setIsLoading(false);
                props.onHide?.();
                setShowVerifyEmailModal(true);
            },
            (err: any) => {
                setIsLoading(false);
                setNewEmailErrorMsg(err?.data?.message);
            }
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleSendUpdateEmailCode();
    };

    return (
        <>
            <Dialog open={props.show} onOpenChange={(open) => { if (!open) handleHideModal(); }}>
                <DialogContent className="gs-tag-dialog gs-tag-dialog--wide">
                    <div className="gs-tag-dialog__header">
                        <h4 className="gs-tag-dialog__title" id="update-email-modal-title">
                            {HEADINGS.UPDATE_EMAIL}
                        </h4>
                    </div>

                    <div className="gs-tag-dialog__body gs-update-email-dialog__body">
                        {screenState === "update-email-instruction" && (
                            <>
                                <div className="gs-update-email-warning" role="alert">
                                    <em className="icon ni ni-alert-fill gs-update-email-warning__icon" aria-hidden />
                                    <p id="update-email-instruction-waring-message" className="gs-update-email-warning__text">
                                        {CHANGE_EMAIL_WARNING_MSG}
                                    </p>
                                </div>

                                <div className="gs-update-email-section">
                                    <h6 id="update-email-instruction-sub-heading-1">
                                        {UPDATE_PROFILE_DESCRIPTION.YOUR_EMAIL_USED_TO}
                                    </h6>
                                    <ul>
                                        <li id="update-email-instruction-1">{UPDATE_PROFILE_DESCRIPTION.LOGIN_TO_GREENSIGN}</li>
                                        <li id="update-email-instruction-2">
                                            {UPDATE_PROFILE_DESCRIPTION.RECEIVE_ENVELOPE_WITH_ENCLOSED_DOCUMENT}
                                        </li>
                                    </ul>
                                </div>

                                <div className="gs-update-email-section">
                                    <h6 id="update-email-instruction-sub-heading-2">
                                        {UPDATE_PROFILE_DESCRIPTION.ENVELOPES_SENT_AND_RECEIVED_PREVIOUSLY}
                                    </h6>
                                    <ul>
                                        <li id="update-email-instruction-3">{UPDATE_PROFILE_DESCRIPTION.APPEAR_IN_ENVELOPE_LIST}</li>
                                        <li id="update-email-instruction-4">{UPDATE_PROFILE_DESCRIPTION.NOTIFICATION_ABOUT_ENVELOPE}</li>
                                    </ul>
                                </div>

                                <div className="gs-update-email-section">
                                    <h6 id="update-email-instruction-sub-heading-3">
                                        {UPDATE_PROFILE_DESCRIPTION.SOMEONE_SENDS_NEW_ENVELOPE_TO_OLD_EMAIL}
                                    </h6>
                                    <ul>
                                        <li id="update-email-instruction-5">{UPDATE_PROFILE_DESCRIPTION.NOT_APPEAR_IN_ENVELOPE_LIST}</li>
                                        <li id="update-email-instruction-6">{UPDATE_PROFILE_DESCRIPTION.NOTIFICATION_TO_OLD_EMAIL}</li>
                                    </ul>
                                </div>
                            </>
                        )}

                        {screenState === "verify-current-email" && (
                            <>
                                <p className="gs-update-email-message" id="update-email-message">
                                    {UPDATE_PROFILE_DESCRIPTION.MESSAGE_FOR_SSO_USER_GUIDE}
                                </p>
                                <div className="gs-update-email-field">
                                    <label htmlFor="verify-email-modal-code-input" id="verify-email-modal-code-label">
                                        {LABELS.VERIFICATION_CODE}
                                    </label>
                                    <input
                                        id="verify-email-modal-code-input"
                                        type="text"
                                        value={code}
                                        maxLength={4}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Enter 4-digit code"
                                        onChange={(e) => {
                                            setCodeErrorMsg("");
                                            setCode(e.target.value);
                                        }}
                                        className="gs-tag-dialog__input"
                                    />
                                    {codeErrorMsg ? (
                                        <p className="gs-tag-dialog__error" id="verify-email-modal-err-msg">
                                            {codeErrorMsg}
                                        </p>
                                    ) : null}
                                </div>
                            </>
                        )}

                        {screenState === "update-email" && (
                            <>
                                <p className="gs-update-email-message" id="update-email-message">
                                    {UPDATE_PROFILE_DESCRIPTION.MESSAGE_FOR_USER_GUIDE}
                                </p>
                                <div className="gs-update-email-field">
                                    <label htmlFor="update-old-email-input" id="update-old-email-label">
                                        {LABELS.OLD_EMAIL}
                                    </label>
                                    <input
                                        disabled
                                        type="text"
                                        value={getemail_address()}
                                        className="gs-tag-dialog__input"
                                        id="update-old-email-input"
                                    />
                                </div>
                                <div className="gs-update-email-field">
                                    <label htmlFor="update-new-email-input" id="update-new-email-label">
                                        {LABELS.NEW_EMAIL}
                                    </label>
                                    <input
                                        id="update-new-email-input"
                                        type="text"
                                        autoFocus
                                        value={newEmail}
                                        onKeyDown={handleKeyDown}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        className="gs-tag-dialog__input"
                                    />
                                    {newEmailErrorMsg ? (
                                        <p className="gs-tag-dialog__error" id="update-email-error-message">
                                            {newEmailErrorMsg}
                                        </p>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="gs-tag-dialog__footer">
                        {screenState === "update-email-instruction" && (
                            <AppButton btnText={BUTTONS.CONTINUE} className="gs-tag-dialog__submit btn-primary" onClick={handleContinueToEmail} />
                        )}
                        {screenState === "verify-current-email" && (
                            <AppButton
                                btnText={BUTTONS.VERIFY}
                                isLoading={isLoading}
                                onClick={handleVerifyCurrentEmailCode}
                                className="gs-tag-dialog__submit btn-primary"
                            />
                        )}
                        {screenState === "update-email" && (
                            <AppButton
                                btnText={BUTTONS.SEND_CODE}
                                isLoading={isLoading}
                                onClick={handleSendUpdateEmailCode}
                                className="gs-tag-dialog__submit btn-primary"
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <VerifyEmailModal
                setEmailAddress={props.setEmailAddress}
                setScreenState={setScreenState}
                email={newEmail}
                show={showVerifyEmailModal}
                onHide={() => setShowVerifyEmailModal(false)}
                setShowEditEmailModal={props.setShowEditEmailModal}
                handleResendCode={handleSendUpdateEmailCode}
            />
        </>
    );
};

export default UpdateEmail;
